import { useEffect } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "hooks/useCustomNavigation";
import { Loader } from "components/UI/atoms";
import {
  Breadcrumb,
  FallbackLoader,
  GroupedDiscountList,
  GroupedDiscountListEmpty,
  Pagination
} from "components/UI/molecules";
import {
  BrandCash,
  BrandDetailsSidebar,
  DiscountFilters,
  DiscountHeader,
  HeaderBrandDetails,
  ListingsModalFeedback,
  MobileBrandDescription,
  MobileBrandDetails,
  Recommended
} from "components/UI/organisms";
import { ReactHelmetWithProvider } from "providers/ReactHelmetProvider";
import { withApolloProvider } from "providers/ApolloClientProvider";
import { useWindowDimensions } from "hooks/useWindowDimensions";
import { GRAPHQL_CMS_BASE_URL } from "constants/endpoints";
import { INTERNAL_PATHS } from "routes/paths";
import { useGetBrandSEO } from "./utils/useGetBrandSEO";
import { useGetBrandDetails } from "./utils/useGetBrandDetails";
import { useGetBrandDiscounts } from "./utils/useGetBrandDiscounts";
import { BREADCRUMB_PATHS, PAGE_SIZE, SORT_LIST } from "./utils/constants";
import { generatePageDescription, generatePageTitle } from "./utils/helmet";
import "./index.scss";

const BrandDetails = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { width } = useWindowDimensions();

  const { data: brandSEO } = useGetBrandSEO(pathname);
  const { brandDetails, isLoading: isBrandDetailsLoading } =
    useGetBrandDetails(pathname);
  const {
    productSource,
    productSummaries,
    filters,
    isLoading: isProductSummariesLoading,
    pagination,
    setPage,
    fetchProductSummaries
  } = useGetBrandDiscounts({
    brandSlug: brandDetails?.path,
    productSourceId: brandDetails?.productSourceId
  });

  const { instantConfig, instantConfigId } = brandDetails ?? {};
  const { denominations, increment, max_value } = instantConfig ?? {};
  const affiliateUrl = productSource.affiliate_url;

  const isReady = brandDetails && productSource && productSummaries && brandSEO;
  const shouldShowInResponsive = width < 1200;
  const shouldShowDiscountList = brandDetails?.quantity_available > 0;
  const shouldShowCash =
    instantConfigId && (denominations.length || (increment && max_value));

  useEffect(() => {
    const brandNotFound = !brandDetails & !isBrandDetailsLoading;
    if (brandNotFound) navigate(INTERNAL_PATHS.NOT_FOUND);
  }, [isBrandDetailsLoading, brandDetails, navigate]);

  if (!isReady) return <FallbackLoader />;

  return (
    <>
      <ReactHelmetWithProvider>
        <title>{generatePageTitle(brandDetails)}</title>
        <meta
          name="description"
          content={generatePageDescription(brandDetails)}
        />
        <link rel="canonical" href={process.env.REACT_APP_URL + pathname} />
      </ReactHelmetWithProvider>
      <div className="brand-details-header">
        <Breadcrumb
          customClassname="breadcrumb-brand-details"
          list={[
            ...BREADCRUMB_PATHS,
            {
              label: `${brandDetails.name} Gift Cards`
            }
          ]}
        />
      </div>
      <div className="brand-details-container">
        <HeaderBrandDetails iconUrl={brandDetails.iconUrl} />
        <main className="brand-details">
          <BrandDetailsSidebar
            brandDetails={brandDetails}
            brandTerms={productSource.terms}
          />
          <div className="brand-content">
            {shouldShowInResponsive && (
              <MobileBrandDescription
                name={brandDetails.name}
                description={brandDetails.description}
                iconUrl={brandDetails.iconUrl}
              />
            )}
            {shouldShowCash && (
              <BrandCash affiliateUrl={affiliateUrl} {...brandDetails} />
            )}
            <DiscountHeader brandDetails={brandDetails} brandSEO={brandSEO} />
            {shouldShowDiscountList && (
              <>
                <DiscountFilters
                  sortList={SORT_LIST}
                  setPage={setPage}
                  {...pagination}
                  {...filters}
                />
                {isProductSummariesLoading ? (
                  <Loader variant="secondary" id="listings-loader" />
                ) : (
                  <>
                    <GroupedDiscountList
                      brandDetails={brandDetails}
                      productSummaries={productSummaries}
                      onSuccessInAddingToCart={fetchProductSummaries}
                    />
                    <Pagination
                      onPageChange={setPage}
                      pageSize={PAGE_SIZE}
                      className="pagination"
                      {...pagination}
                    />
                    <ListingsModalFeedback brandName={brandDetails.name} />
                    {!productSummaries.length && <GroupedDiscountListEmpty />}
                  </>
                )}
              </>
            )}
            {shouldShowInResponsive ? (
              <MobileBrandDetails
                brandDetails={brandDetails}
                brandTerms={productSource.terms}
              />
            ) : (
              <Recommended legacyBrandId={brandDetails.legacyBrandId} />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default withApolloProvider(BrandDetails, {
  graphqlEndpoint: GRAPHQL_CMS_BASE_URL
});
